import { INITIAL_FORM_DATA } from '@/clients/onboarding/components/constants';
import { calculateAgeFromDateString } from '@/clients/onboarding/components/date-utils';
import type { FormData } from '@/clients/onboarding/components/types';

export const CALORIESCIENCE_EMAIL_DOMAIN = 'caloriescience.ai';

export type DemoPersonaId = 'weight_loss' | 'type_2_diabetes' | 'pregnancy';

export type DemoPersonaDefinition = {
  id: DemoPersonaId;
  label: string;
  description: string;
  /** Clinical + identity fields; email/phone are generated per load. */
  profile: Omit<Partial<FormData>, 'email' | 'phone' | 'age'> & {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  };
};

/** True when the signed-in nutritionist uses a CalorieScience email. */
export function isCalorieScienceEmail(email: string | null | undefined): boolean {
  const normalized = String(email ?? '').trim().toLowerCase();
  if (!normalized.includes('@')) return false;
  const domain = normalized.slice(normalized.lastIndexOf('@') + 1);
  return domain === CALORIESCIENCE_EMAIL_DOMAIN || domain.endsWith(`.${CALORIESCIENCE_EMAIL_DOMAIN}`);
}

function dateOfBirthYearsAgo(years: number, month = 6, day = 15): string {
  const today = new Date();
  const year = today.getFullYear() - years;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function randomDigits(length: number): string {
  let result = '';
  for (let index = 0; index < length; index += 1) {
    result += String(Math.floor(Math.random() * 10));
  }
  return result;
}

/** Unique client email so backend uniqueness checks pass on every demo load. */
export function buildDemoPersonaEmail(personaId: DemoPersonaId): string {
  const stamp = Date.now().toString(36);
  const suffix = randomDigits(4);
  return `demo+${personaId}.${stamp}.${suffix}@example.com`;
}

/**
 * UK mobile number shape accepted by the shared phone validator — national digits only (no dial code).
 * Matches how the Add Client phone field stores values.
 */
export function buildDemoPersonaPhone(): string {
  return `7911123${randomDigits(3)}`;
}

export const DEMO_PERSONAS: readonly DemoPersonaDefinition[] = [
  {
    id: 'weight_loss',
    label: 'Weight loss',
    description: 'Adult male, overweight, moderate deficit',
    profile: {
      firstName: 'Demo',
      lastName: 'WeightLoss',
      dateOfBirth: dateOfBirthYearsAgo(42),
      gender: 'male',
      sexAtBirth: 'male',
      location: 'united-kingdom',
      cityOrCounty: 'London',
      phone_country_code: '+44',
      preferredMeasurementSystem: 'metric',
      weightKg: '98',
      heightCm: '178',
      waistCircumferenceCm: '104',
      medicalConditions: 'High blood pressure,hyperlipidemia',
      weightLossGoal: 'moderate_loss',
      healthGoal: 'Sustainable fat loss while preserving lean mass',
      allergies: 'dairy',
      foodIntolerances: 'gluten',
      dietaryPreferences: 'high_protein',
      cuisineTypes: 'british',
      lactationStatus: '',
    },
  },
  {
    id: 'type_2_diabetes',
    label: 'Type 2 diabetes',
    description: 'Adult female with T2D clinical targets',
    profile: {
      firstName: 'Demo',
      lastName: 'Type2Diabetes',
      dateOfBirth: dateOfBirthYearsAgo(55, 3, 8),
      gender: 'female',
      sexAtBirth: 'female',
      location: 'united-kingdom',
      cityOrCounty: 'Manchester',
      phone_country_code: '+44',
      preferredMeasurementSystem: 'metric',
      weightKg: '82',
      heightCm: '162',
      waistCircumferenceCm: '96',
      medicalConditions: 'diabetes',
      diabetesType: 'Type 2',
      weightLossGoal: 'mild_loss',
      healthGoal: 'Improve glycaemic control and cardiometabolic health',
      allergies: 'dairy',
      foodIntolerances: 'gluten',
      dietaryPreferences: 'low_carb',
      cuisineTypes: 'british',
      pregnancyStatus: '',
      pregnancyTrimester: '',
      prePregnancyWeightKg: '',
      lactationStatus: 'none',
      twinPregnancy: '',
    },
  },
  {
    id: 'pregnancy',
    label: 'Pregnancy',
    description: 'Second-trimester singleton pregnancy',
    profile: {
      firstName: 'Demo',
      lastName: 'Pregnancy',
      dateOfBirth: dateOfBirthYearsAgo(31, 9, 22),
      gender: 'female',
      sexAtBirth: 'female',
      location: 'united-kingdom',
      cityOrCounty: 'Bristol',
      phone_country_code: '+44',
      preferredMeasurementSystem: 'metric',
      weightKg: '72',
      heightCm: '165',
      medicalConditions: '',
      pregnancyStatus: 'pregnant',
      pregnancyTrimester: 'second',
      prePregnancyWeightKg: '64',
      lactationStatus: 'none',
      twinPregnancy: 'singleton',
      weightLossGoal: 'maintain',
      healthGoal: 'Support healthy pregnancy nutrition',
      allergies: 'dairy',
      foodIntolerances: 'gluten',
      dietaryPreferences: 'vegetarian',
      cuisineTypes: 'british',
    },
  },
] as const;

export function buildDemoPersonaFormData(personaId: DemoPersonaId): FormData {
  const persona = DEMO_PERSONAS.find((entry) => entry.id === personaId);
  if (!persona) {
    throw new Error(`Unknown demo persona: ${personaId}`);
  }

  const dateOfBirth = persona.profile.dateOfBirth;
  const age = calculateAgeFromDateString(dateOfBirth);
  const email = buildDemoPersonaEmail(personaId);
  const phone = buildDemoPersonaPhone();

  return {
    ...INITIAL_FORM_DATA,
    ...persona.profile,
    email,
    phone,
    age: age !== null ? String(age) : '',
  };
}
